import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { saveProductIngredients } from "@/hooks/use-ingredients";
import { saveProductTags } from "@/hooks/use-tags";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { CategorySelect } from "./CategorySelect";
import { IngredientsSelect } from "./IngredientsSelect";
import { ProductImageManager } from "./ProductImageManagerAddition";
import { TagsSelect } from "./TagsSelect";

const productSchema = z.object({
	name: z
		.string()
		.min(3, { message: "Product name must be at least 3 characters." }),
	description: z
		.string()
		.min(10, { message: "Description must be at least 10 characters." }),
	price: z.coerce
		.number()
		.positive({ message: "Price must be a positive number." }),
	caseQuantity: z.coerce.number().int().min(1, {
		message: "Case quantity must be at least 1.",
	}),
	msrp: z.coerce
		.number()
		.positive({ message: "MSRP must be a positive number." }),
	categoryId: z.string().uuid({ message: "Please select a valid category." }),
	tags: z.array(z.string().uuid()).default([]),
	ingredients: z.array(z.string().uuid()).default([]),
	sampleAvailable: z.boolean().default(false),
	sampleQuantity: z.coerce.number().int().min(1).optional(),
	sampleCost: z.coerce.number().min(0).optional(),
});

type ProductFormValues = z.infer<typeof productSchema>;

export function AddProductForm() {
	const { user } = useAuth();
	const { toast } = useToast();
	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [orderedImages, setOrderedImages] = useState<ProductImageType[]>([]);

	const form = useForm<ProductFormValues>({
		resolver: zodResolver(productSchema),
		defaultValues: {
			name: "",
			description: "",
			price: 0,
			caseQuantity: 1,
			msrp: 0,
			categoryId: "",
			tags: [],
			ingredients: [],
			sampleAvailable: false,
			sampleQuantity: 1,
			sampleCost: 0,
		},
	});

	const uploadImages = async (productId: string): Promise<string[]> => {
		if (!selectedFiles.length) return [];

		setIsUploading(true);
		const imageUrls: string[] = [];
		const maxRetries = 3;

		try {
			for (let i = 0; i < selectedFiles.length; i++) {
				const file = selectedFiles[i];
				let retryCount = 0;
				let uploaded = false;

				while (!uploaded && retryCount < maxRetries) {
					try {
						const fileExt = file.name.split(".").pop();
						const fileName = `${
							user!.id
						}/${productId}/${Date.now()}-${i}.${fileExt}`;
						const filePath = fileName;

						// Upload image to storage with retry logic
						const { error: uploadError, data } =
							await supabase.storage
								.from("product_images")
								.upload(filePath, file, {
									cacheControl: "3600",
									upsert: false,
								});

						if (uploadError) throw uploadError;

						const {
							data: { publicUrl },
						} = supabase.storage
							.from("product_images")
							.getPublicUrl(filePath);

						imageUrls.push(publicUrl);
						uploaded = true;

						// Update progress
						setUploadProgress(
							((i + 1) / selectedFiles.length) * 100
						);
					} catch (error) {
						retryCount++;
						if (retryCount === maxRetries) throw error;
						// Wait before retrying
						await new Promise((resolve) =>
							setTimeout(resolve, 1000 * retryCount)
						);
					}
				}
			}

			return imageUrls;
		} catch (error) {
			console.error("Error uploading images:", error);
			toast({
				title: "Error uploading images",
				description: "Some images failed to upload. Please try again.",
				variant: "destructive",
			});
			throw error;
		} finally {
			setIsUploading(false);
			setUploadProgress(0);
		}
	};

	const handleImageReorder = async (images: ProductImageType[]) => {
		// Only update if we have new images
		if (images.length > 0) {
			setOrderedImages(images);
		}
	};

	const onSubmit = async (data: ProductFormValues) => {
		if (!user) {
			toast({
				title: "Authentication required",
				description: "You must be logged in to add products.",
				variant: "destructive",
			});
			return;
		}

		try {
			setIsSubmitting(true);

			// Insert product
			const { data: product, error: productError } = await supabase
				.from("products")
				.insert({
					name: data.name,
					description: data.description,
					price: parseFloat(String(data.price)),
					case_quantity: data.caseQuantity,
					msrp: data.msrp || null,
					category_id: data.categoryId,
					supplier_id: user.id,
					sample_available: data.sampleAvailable,
					sample_quantity: data.sampleAvailable
						? data.sampleQuantity
						: null,
					sample_cost: data.sampleAvailable ? data.sampleCost : null,
				})
				.select()
				.single();

			if (productError) throw productError;

			// Save product tags
			if (data.tags.length > 0) {
				await saveProductTags(product.id, data.tags);
			}

			// Save product ingredients
			if (data.ingredients.length > 0) {
				await saveProductIngredients(product.id, data.ingredients);
			}

			// Upload images if any
			if (selectedFiles.length > 0) {
				const imageUrls = await uploadImages(product.id);

				const productImages = imageUrls.map((url, index) => ({
					product_id: product.id,
					image_url: url,
					is_primary: index === 0,
					sequence: index,
				}));

				const { error: imagesError } = await supabase
					.from("product_images")
					.insert(productImages);

				if (imagesError) throw imagesError;
			}

			toast({
				title: "Product added",
				description: "Your product has been added successfully.",
			});

			navigate("/dashboard/products");
		} catch (error: any) {
			toast({
				title: "Error adding product",
				description:
					error.message ||
					"An error occurred while adding the product.",
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="space-y-8">
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Product Name - Full width */}
						<div className="md:col-span-2">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Product Name</FormLabel>
										<FormControl>
											<Input
												placeholder="Enter product name"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Description - Full width */}
						<div className="md:col-span-2">
							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Description</FormLabel>
										<FormControl>
											<Textarea
												placeholder="Describe your product"
												className="min-h-[120px]"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Case Quantity on its own row */}
						<div className="md:col-span-2">
							<FormField
								control={form.control}
								name="caseQuantity"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											Item Quantity Per Case
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												min="1"
												step="1"
												placeholder="1"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Price and MSRP on same row */}
						<div className="md:col-span-2 grid grid-cols-2 gap-6">
							<FormField
								control={form.control}
								name="price"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											Price per Unit ($)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												step="0.01"
												min="0"
												placeholder="0.00"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="msrp"
								render={({ field }) => (
									<FormItem>
										<FormLabel>MSRP ($)</FormLabel>
										<FormControl>
											<Input
												type="number"
												step="0.01"
												min="0"
												placeholder="0.00"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Category Selection - now spans full width */}
						<div className="md:col-span-2">
							<FormField
								control={form.control}
								name="categoryId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Category</FormLabel>
										<FormControl>
											<CategorySelect
												value={field.value}
												onChange={field.onChange}
												placeholder="Select a category"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Tags Selection - spans full width */}
						<div className="md:col-span-2">
							<FormField
								control={form.control}
								name="tags"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Product Tags</FormLabel>
										<FormControl>
											<TagsSelect
												value={field.value}
												onChange={field.onChange}
												placeholder="Select product tags"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Ingredients Selection - spans full width */}
						<div className="md:col-span-2">
							<FormField
								control={form.control}
								name="ingredients"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											Product Ingredients
										</FormLabel>
										<FormControl>
											<IngredientsSelect
												value={field.value}
												onChange={field.onChange}
												placeholder="Select or create ingredients"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Sample Information Section */}
						<div className="md:col-span-2">
							<div className="p-4 bg-blue-50 rounded-lg mb-4">
								<h3 className="text-lg font-medium mb-2">
									Sample Information
								</h3>
								<p className="text-sm text-gray-600 mb-4">
									Offer product samples to retailers to try
									before making larger orders
								</p>

								<FormField
									control={form.control}
									name="sampleAvailable"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center space-x-3 space-y-0 mb-4">
											<FormControl>
												<input
													type="checkbox"
													checked={field.value}
													onChange={field.onChange}
													className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
												/>
											</FormControl>
											<FormLabel className="cursor-pointer">
												This product has samples
												available
											</FormLabel>
										</FormItem>
									)}
								/>

								{form.watch("sampleAvailable") && (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<FormField
											control={form.control}
											name="sampleQuantity"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														Sample Quantity
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															min="1"
															step="1"
															placeholder="1"
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="sampleCost"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														Sample Cost ($)
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															step="0.01"
															min="0"
															placeholder="0.00"
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Image Upload Section */}
					<ProductImageManager
						selectedFiles={selectedFiles}
						setSelectedFiles={setSelectedFiles}
						isUploading={isUploading}
						uploadProgress={uploadProgress}
						onReorder={handleImageReorder}
					/>

					{/* Submit Button */}
					<div className="flex justify-end gap-4">
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate("/dashboard/products")}
							disabled={isSubmitting || isUploading}>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="accent"
							disabled={isSubmitting || isUploading}>
							{isSubmitting || isUploading ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{isUploading
										? "Uploading..."
										: "Adding Product..."}
								</>
							) : (
								"Add Product"
							)}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
}
